// src/features/legacy-reports/components/LegacyReportsPage.tsx
//
// Read-only Legacy Reports compatibility surface over the UNCHANGED
// /v1/reports1 routes. Single route, internal tabs — minimal by design.
// Nothing here imports from features/analytics (no-blending, ADR-003 §9); the
// banner's "Analytics" link is plain navigation, not a data/component import.

import { useMemo, useState } from 'react';
import { Alert, Anchor, Group, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  ANALYTICS_START_DISPLAY,
  LEGACY_METHODOLOGY_NOTICE,
  LEGACY_REPORTS_QUERY_BOUNDARY_DISPLAY,
  LEGACY_REPORTS_QUERY_BOUNDARY_TO_SECONDS,
  LEGACY_RETAINED_RANGE_COPY,
  LEGACY_RETAINED_RANGE_COPY_ENABLED,
} from '@/lib/constants/legacyReports';
import { clampRangeToBoundary, presetToRange, type UtcRange } from '@/shared/utils/utcRange';
import { useLegacyAccountReport } from '../useLegacyAccountReport';
import { useLegacySubaccountsReport } from '../useLegacySubaccountsReport';
import { useLegacyReportsEligibility } from '../useLegacyReportsEligibility';
import { LegacyAccountReportView } from './LegacyAccountReportView';
import { LegacyMediaTab } from './LegacyMediaTab';
import { LegacyRangeControl, type LegacyRangePreset } from './LegacyRangeControl';

function LegacyAccountTab({ cId, start, end }: { cId: number | null; start: number; end: number }) {
  const { report, isLoading, isError, error } = useLegacyAccountReport(cId, start, end);
  return (
    <LegacyAccountReportView report={report} isLoading={isLoading} isError={isError} error={error} />
  );
}

function LegacySubaccountsTab({
  cId,
  start,
  end,
}: {
  cId: number | null;
  start: number;
  end: number;
}) {
  const { report, isLoading, isError, error } = useLegacySubaccountsReport(cId, start, end, true);
  // /subaccounts returns totals only — omit the always-empty breakdown panels.
  return (
    <LegacyAccountReportView
      report={report}
      isLoading={isLoading}
      isError={isError}
      error={error}
      showBreakdowns={false}
    />
  );
}

export function LegacyReportsPage() {
  const { eligible } = useLegacyReportsEligibility();
  const { authData, selectedCId, selectedCustomer, subcustomers } = useAuth();
  const navigate = useNavigate();

  const [preset, setPreset] = useState<LegacyRangePreset>('last30d');
  const [customRange, setCustomRange] = useState<UtcRange | null>(null);

  const range = useMemo<UtcRange>(() => {
    const base =
      preset === 'custom' && customRange
        ? customRange
        : presetToRange(preset === 'custom' ? 'last30d' : preset, Math.floor(Date.now() / 1000));
    // ADR-011: the entire interactive Legacy Reports section is capped at the
    // query boundary T — any preset/default/saved range extending past T is
    // truncated to T (never rejected). Analytics owns everything from T onward.
    return clampRangeToBoundary(base, LEGACY_REPORTS_QUERY_BOUNDARY_TO_SECONDS);
  }, [preset, customRange]);

  const isMasterAccountSelected = !!selectedCustomer && !selectedCustomer.cfg.parentCId;
  const showSubaccountsTab =
    isMasterAccountSelected &&
    !!authData?.cpuser.permissions.subcustomerRead &&
    subcustomers.length > 0;

  if (!eligible) {
    return (
      <Stack gap="md">
        <Title order={2}>Legacy Reports</Title>
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          Legacy Reports are not available for this account.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>Legacy Reports</Title>
        <LegacyRangeControl
          preset={preset}
          onPresetChange={setPreset}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </Group>

      {/* ADR-011 query-boundary banner + permanent methodology notice (ADR-003 §9). */}
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        <Stack gap={4}>
          <Text size="sm">
            Legacy Reports cover through {LEGACY_REPORTS_QUERY_BOUNDARY_DISPLAY}.{' '}
            <Anchor component="button" type="button" onClick={() => navigate({ to: '/analytics' })}>
              Analytics
            </Anchor>{' '}
            begins {ANALYTICS_START_DISPLAY}.
          </Text>
          <Text size="sm" c="dimmed">
            {LEGACY_METHODOLOGY_NOTICE}
            {LEGACY_RETAINED_RANGE_COPY_ENABLED && <> {LEGACY_RETAINED_RANGE_COPY}</>}
          </Text>
        </Stack>
      </Alert>

      <Tabs defaultValue="account" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="account">Account</Tabs.Tab>
          <Tabs.Tab value="media">Media</Tabs.Tab>
          {showSubaccountsTab && <Tabs.Tab value="subaccounts">Sub-Accounts</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="account" pt="md">
          <LegacyAccountTab cId={selectedCId} start={range.from} end={range.to} />
        </Tabs.Panel>
        <Tabs.Panel value="media" pt="md">
          <LegacyMediaTab cId={selectedCId} start={range.from} end={range.to} />
        </Tabs.Panel>
        {showSubaccountsTab && (
          <Tabs.Panel value="subaccounts" pt="md">
            <LegacySubaccountsTab cId={selectedCId} start={range.from} end={range.to} />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}
